import TableSearch from "@rio-cloud/rio-uikit/TableSearch"
import TableToolbar from "@rio-cloud/rio-uikit/TableToolbar"
import ButtonDropdown from "@rio-cloud/rio-uikit/ButtonDropdown"
import Select from "@rio-cloud/rio-uikit/Select"
import { MenuItemProps } from "@rio-cloud/rio-uikit/MenuItem"
import { FormattedMessage, useIntl } from "react-intl"
import * as React from "react"
import { useEffect, useMemo, useRef, useState } from "react"
import { VolumeForecastDownloadButton } from "../../VolumeForecast/VolumeForecastDownloadButton"
import { MainRunButtonDropdown } from "../../MainRun/MainRunButtonDropdown"
import DatePicker from "@rio-cloud/rio-uikit/DatePicker"
import moment from "moment"
import { CSVExportButtonDropdown } from "../CSVExport/CSVExportButtonDropdown"
import {
    TransportOrder,
    TransportOrderType
} from "../../../model/transportorder/transportOrder"
import { Moment } from "moment/moment"
import {
    extractTableParamsFromUrl,
    TransportOrderDateFilterType
} from "../../../redux/transportorder.slice"
import { v4 as uuid } from "uuid"
import { FreightPaymentDetailsButton } from "@/components/FreightPaymentDetails/FreightPaymentDetailsButton"
import { TableSearchPendingState } from "./TableSearchPendingState"
import { ConfirmationUploadButtonDropdown } from "../ConfirmationUpload/ConfirmationUploadButtonDropdown"
import { useFeatureToggle } from "@/configuration/featuretoggle/hooks"
import classNames from "@rio-cloud/rio-uikit/utils/classNames"

interface TableToolBarProps {
    onSearchPhraseChange: (value: string) => void
    location: string
    activeDateFilterType: TransportOrderDateFilterType
    onDateFilterTypeChange: (dateFilterType: TransportOrderDateFilterType) => void
    orderDateFrom: Date | undefined
    onChangeOrderDateFrom: (date: Date) => void
    orderDateTo: Date | undefined
    onChangeOrderDateTo: (date: Date) => void
    cargoTypes: TransportOrderType[]
    onCargoTypesChange: (cargoTypes: TransportOrderType[]) => void
    transportOrders: TransportOrder[]
}

export const TRANSPORT_ORDER_TABLE_SEARCH_TEST_ID = "TRANSPORT_ORDER_TABLE_SEARCH_TEST_ID"
export const TRANSPORT_ORDER_TABLE_CARGO_TYPE_FILTER_TEST_ID = "TRANSPORT_ORDER_TABLE_CARGO_TYPE_FILTER_TEST_ID"
export const TRANSPORT_ORDER_TABLE_DATE_PICKER_FROM_ID = uuid()
export const TRANSPORT_ORDER_TABLE_DATE_FILTER_BUTTON_TEST_ID = "TRANSPORT_ORDER_TABLE_DATE_FILTER_BUTTON_TEST_ID"
const TRANSPORT_ORDER_TABLE_DATE_PICKER_FROM_TEST_ID = "TRANSPORT_ORDER_TABLE_DATE_PICKER_FROM_TEST_ID"
const TRANSPORT_ORDER_TABLE_DATE_PICKER_TO_TEST_ID = "TRANSPORT_ORDER_TABLE_DATE_PICKER_TO_TEST_ID"
const SEARCH_DEBOUNCE_DELAY_IN_MS = 250

const DATE_FILTER_OPTIONS: readonly {
    value: TransportOrderDateFilterType
    labelId: string
}[] = [
    {
        value: TransportOrderDateFilterType.CREATED_AT,
        labelId: "transportOrder.overview.toolbar.dateFilter.createdAt",
    },
    {
        value: TransportOrderDateFilterType.EARLIEST_PICKUP_AT,
        labelId: "transportOrder.overview.toolbar.dateFilter.earliestPickupAt",
    },
    {
        value: TransportOrderDateFilterType.LATEST_DELIVERY_AT,
        labelId: "transportOrder.overview.toolbar.dateFilter.latestDeliveryAt",
    }
]

export function TableToolBar(props: TableToolBarProps): React.ReactElement {
    const intl = useIntl()
    const {
        activeDateFilterType,
        location,
        onDateFilterTypeChange,
        onChangeOrderDateFrom,
        onChangeOrderDateTo,
        cargoTypes,
        onCargoTypesChange,
        onSearchPhraseChange,
        orderDateFrom,
        orderDateTo,
        transportOrders
    } = props
    const displayTransportationReportItem = useFeatureToggle("showTransportationReport", false)
    const externalSearchPhrase = extractTableParamsFromUrl(location).searchPhrase || ""
    const searchInputRef = useRef<HTMLInputElement>(null)
    const latestSearchValueRef = useRef(externalSearchPhrase)
    const committedSearchValueRef = useRef(externalSearchPhrase)
    const searchDebounceTimeoutRef = useRef<number | undefined>(undefined)
    const [isSearchPending, setIsSearchPending] = useState(false)

    useEffect(() => {
        latestSearchValueRef.current = externalSearchPhrase
        committedSearchValueRef.current = externalSearchPhrase
        window.clearTimeout(searchDebounceTimeoutRef.current)
        if (searchInputRef.current && searchInputRef.current !== document.activeElement) {
            searchInputRef.current.value = externalSearchPhrase
        }
    }, [externalSearchPhrase])

    const commitSearchPhrase = (value: string): void => {
        window.clearTimeout(searchDebounceTimeoutRef.current)
        searchDebounceTimeoutRef.current = undefined
        if (value === committedSearchValueRef.current) {
            return
        }
        committedSearchValueRef.current = value
        onSearchPhraseChange(value)
    }

    const handleCargoTypeChange = (selectedCargoType: { id: string, label: string } | undefined) => onCargoTypesChange(
        selectedCargoType?.id === TransportOrderType.EMPTIES || selectedCargoType?.id === TransportOrderType.MATERIAL
            ? [selectedCargoType.id]
            : []
    )

    const activeDateFilterLabelId = DATE_FILTER_OPTIONS.find(option => option.value === activeDateFilterType)?.labelId
        ?? "transportOrder.overview.toolbar.dateFilter.createdAt"
    const dateFilterItems: MenuItemProps[] = DATE_FILTER_OPTIONS.map((option) => ({
        onSelect: () => onDateFilterTypeChange(option.value),
        active: option.value === activeDateFilterType,
        value: <FormattedMessage id={option.labelId}/>,
    }))
    const cargoTypeOptions = useMemo(() => [
        {
            id: "ALL",
            label: intl.formatMessage({ id: "transportOrder.overview.toolbar.cargoTypeFilter.all" }),
        },
        {
            id: TransportOrderType.EMPTIES,
            label: intl.formatMessage({ id: "shipment.type.empties" }),
        },
        {
            id: TransportOrderType.MATERIAL,
            label: intl.formatMessage({ id: "shipment.type.materials" }),
        }
    ], [intl])

    return (
        <TableToolbar>
            <div className="table-toolbar-container">
                <div className="table-toolbar-group-left">
                    <div className={classNames(
                        "table-toolbar-column",
                        displayTransportationReportItem && "table-toolbar-column-spacer"
                    )}>
                        <div className={"display-flex flex-row gap-5"}>
                            <div className={"align-self-end"}>
                                <ButtonDropdown
                                    data-testid={TRANSPORT_ORDER_TABLE_DATE_FILTER_BUTTON_TEST_ID}
                                    title={<span className="rioglyph rioglyph-settings"/>}
                                    iconOnly
                                    items={dateFilterItems}
                                />
                            </div>
                            <ToolBarDatePicker
                                testId={TRANSPORT_ORDER_TABLE_DATE_PICKER_FROM_TEST_ID}
                                label={<>
                                    <FormattedMessage id={activeDateFilterLabelId}/>{" "}
                                    <FormattedMessage id="transportOrder.overview.toolbar.date.from"/>:
                                </>}
                                onChange={onChangeOrderDateFrom}
                                selectedDate={orderDateFrom}
                                isValidDate={(from: moment.Moment) => {
                                    // Choosing dates before a certain key date will not return any records, since we did not migrate old records
                                    const dateAvailableForRecords = activeDateFilterType === TransportOrderDateFilterType.CREATED_AT || from.isSameOrAfter("2026-07-12T22:00:00.00+00:00")
                                    const fromBeforeTo = orderDateTo === undefined || from.isBefore(orderDateTo)
                                    return dateAvailableForRecords && fromBeforeTo
                                }}
                            />
                            <ToolBarDatePicker
                                testId={TRANSPORT_ORDER_TABLE_DATE_PICKER_TO_TEST_ID}
                                label={<><FormattedMessage id="transportOrder.overview.toolbar.orderDate.to"/>:</>}
                                onChange={onChangeOrderDateTo}
                                selectedDate={orderDateTo}
                                isValidDate={(to: moment.Moment) => orderDateFrom === undefined || to.isSameOrAfter(orderDateFrom)}
                            />
                        </div>
                    </div>
                    {displayTransportationReportItem && (
                        <div className={"table-toolbar-column"}>
                            <FreightPaymentDetailsButton/>
                        </div>
                    )}
                </div>
                <div className="table-toolbar-group-right">
                    <div className={"table-toolbar-column"}>
                        <MainRunButtonDropdown/>
                    </div>
                    <div className={"table-toolbar-column"}>
                        <VolumeForecastDownloadButton/>
                    </div>
                    <div className={"table-toolbar-column"}>
                        <ConfirmationUploadButtonDropdown/>
                    </div>
                    <div className={"table-toolbar-column table-toolbar-column-spacer"}>
                        <CSVExportButtonDropdown fromDate={orderDateFrom} toDate={orderDateTo} transportOrders={transportOrders}/>
                    </div>
                    <div className="table-toolbar-column" data-testid={TRANSPORT_ORDER_TABLE_CARGO_TYPE_FILTER_TEST_ID}>
                        <Select
                            id="transport-order-cargo-type-filter"
                            name="transport-order-cargo-type-filter"
                            options={cargoTypeOptions}
                            value={cargoTypes.length === 1 ? cargoTypes : ["ALL"]}
                            label={intl.formatMessage({ id: "transportOrder.overview.toolbar.transportAndCargoTypeFilter.label" })}
                            onChange={handleCargoTypeChange}
                        />
                    </div>
                    <div className={"table-toolbar-column"}>
                        <TableSearchPendingState isPending={isSearchPending}>
                            <TableSearch
                                data-testid={TRANSPORT_ORDER_TABLE_SEARCH_TEST_ID}
                                defaultValue={externalSearchPhrase}
                                inputRef={searchInputRef as React.RefObject<HTMLInputElement>}
                                onChange={(value) => {
                                    latestSearchValueRef.current = value
                                    window.clearTimeout(searchDebounceTimeoutRef.current)
                                    if (value === committedSearchValueRef.current) {
                                        setIsSearchPending(false)
                                        return
                                    }
                                    setIsSearchPending(true)
                                    searchDebounceTimeoutRef.current = window.setTimeout(() => {
                                        commitSearchPhrase(value)
                                        setIsSearchPending(false)
                                    }, SEARCH_DEBOUNCE_DELAY_IN_MS)
                                }}
                                onBlur={() => {
                                    commitSearchPhrase(latestSearchValueRef.current)
                                    setIsSearchPending(false)
                                }}
                            />
                        </TableSearchPendingState>
                    </div>
                </div>
            </div>
        </TableToolbar>
    )
}

interface ToolBarDatePickerProps {
    label: React.ReactNode
    selectedDate: Date | undefined
    onChange: (date: Date) => void
    isValidDate: (date: moment.Moment) => boolean
    testId: typeof TRANSPORT_ORDER_TABLE_DATE_PICKER_FROM_TEST_ID | typeof TRANSPORT_ORDER_TABLE_DATE_PICKER_TO_TEST_ID
}

const ToolBarDatePicker = (props: ToolBarDatePickerProps) => {
    const { label, onChange, selectedDate, isValidDate, testId } = props
    const intl = useIntl()
    const datePickerInputProps: React.HTMLProps<HTMLInputElement> = {
        id: TRANSPORT_ORDER_TABLE_DATE_PICKER_FROM_ID,
    }

    if (selectedDate === undefined) {
        datePickerInputProps.value = "" // See https://github.com/arqex/react-datetime/issues/749
    }

    return <div data-testid={testId}>
        <label htmlFor={TRANSPORT_ORDER_TABLE_DATE_PICKER_FROM_ID}>
            {label}
        </label>
        <DatePicker
            locale={intl.locale}
            inputProps={datePickerInputProps}
            onChange={(date: Moment | string) => {
                if (moment.isMoment(date) && date.isValid()) {
                    onChange(date.toDate())
                }
            }}
            value={selectedDate}
            isValidDate={isValidDate}
            clearableInput={false}
            minWidth={170}
            timeFormat={false}
            mandatory={true}
        />
    </div>
}
